import { Trans } from "@lingui/macro";
import cx from "classnames";

import { ACCOUNT, ORDER_TYPES, SIZE_PERCENTS, type OrderType, num, usd } from "../terminalMock";

export type Side = "buy" | "sell";

const PILL = "rounded-full border py-10 text-13 font-semibold transition-colors";
const INFO_ROW = "flex justify-between text-13";

type Props = {
  price: number;
  leverage: number;
  side: Side;
  onSideChange: (side: Side) => void;
  orderType: OrderType;
  onOrderTypeChange: (orderType: OrderType) => void;
  sizePercent: number;
  onSizePercentChange: (percent: number) => void;
};

export function OrderEntryPanel({
  price,
  leverage,
  side,
  onSideChange,
  orderType,
  onOrderTypeChange,
  sizePercent,
  onSizePercentChange,
}: Props) {
  const isBuy = side === "buy";
  const isEmpty = sizePercent === 0;

  const notional = ACCOUNT.availableToTrade * leverage * (sizePercent / 100);
  const size = notional / price;
  const positionPnl = (price - ACCOUNT.entryPrice) * ACCOUNT.positionSize;
  const liquidationPrice = price * (isBuy ? 0.962 : 1.038);

  return (
    <section className="t-panel t-anim-scale-1 flex flex-col gap-14 p-16">
      <div className="grid grid-cols-2 gap-10">
        {[<Trans key="cross">Cross</Trans>, `${leverage}×`].map((label, index) => (
          <button
            key={index}
            type="button"
            className={cx(PILL, "t-pill-hover")}
            style={{ color: "var(--t-deep)", background: "var(--t-panel3)", borderColor: "var(--t-mid)" }}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-18 border-b pb-8" style={{ borderColor: "var(--t-line)" }}>
        {ORDER_TYPES.map((item) => {
          const isActive = orderType === item;

          return (
            <button
              key={item}
              type="button"
              onClick={() => onOrderTypeChange(item)}
              className="border-b-2 py-4 text-[13.5px] font-semibold transition-colors"
              style={{
                color: isActive ? "var(--t-act)" : "var(--t-mut)",
                borderColor: isActive ? "var(--t-act)" : "transparent",
              }}
            >
              {item}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 rounded-full p-4" style={{ background: "var(--t-panel3)" }}>
        <button
          type="button"
          onClick={() => onSideChange("buy")}
          className="rounded-full py-11 text-[13.5px] font-bold transition-all duration-200"
          style={
            isBuy
              ? { background: "var(--t-long)", color: "#fff", boxShadow: "0 6px 14px var(--t-long-glow)" }
              : { background: "transparent", color: "var(--t-mut)" }
          }
        >
          <Trans>Buy / Long</Trans>
        </button>
        <button
          type="button"
          onClick={() => onSideChange("sell")}
          className="rounded-full py-11 text-[13.5px] font-bold transition-all duration-200"
          style={
            !isBuy
              ? { background: "var(--t-short)", color: "#fff", boxShadow: "0 6px 14px var(--t-short-glow)" }
              : { background: "transparent", color: "var(--t-mut)" }
          }
        >
          <Trans>Sell / Short</Trans>
        </button>
      </div>

      <div className="flex flex-col gap-9">
        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Available to trade</Trans>
          </span>
          <span className="t-nums font-semibold" style={{ color: "var(--t-tx)" }}>
            {num(ACCOUNT.availableToTrade, 2)} USDC
          </span>
        </div>
        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Current position</Trans>
          </span>
          <span className="t-nums font-semibold" style={{ color: "var(--t-tx)" }}>
            {ACCOUNT.positionSize} BTC
          </span>
        </div>
      </div>

      <div
        className="t-size-field flex items-center gap-8 rounded-10 border px-13 py-11 transition-shadow"
        style={{ borderColor: "var(--t-mid)", background: "var(--t-panel2)" }}
      >
        <span className="text-13" style={{ color: "var(--t-mut)" }}>
          <Trans>Size</Trans>
        </span>
        <span className="t-display t-nums ml-auto text-[22px]" style={{ color: "var(--t-tx)" }}>
          {num(size, 4)}
        </span>
        <span
          className="rounded-full px-10 py-4 text-12 font-semibold"
          style={{ color: "var(--t-deep)", background: "var(--t-chip)" }}
        >
          BTC ▾
        </span>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex justify-end text-12 font-semibold" style={{ color: "var(--t-act)" }}>
          {sizePercent}%
        </div>

        <div className="relative h-6 rounded-full" style={{ background: "var(--t-chip)" }}>
          <span
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-300"
            style={{ width: `${sizePercent}%`, background: "var(--t-fill)" }}
          />
          <span
            className="absolute top-1/2 -ml-8 -mt-8 h-16 w-16 rounded-full border-[3px] transition-[left] duration-300"
            style={{
              left: `${sizePercent}%`,
              background: "var(--t-panel)",
              borderColor: "#066fd2",
              boxShadow: "var(--t-knob-shadow)",
            }}
          />
        </div>

        <div className="flex justify-between">
          {SIZE_PERCENTS.map((percent) => (
            <button
              key={percent}
              type="button"
              onClick={() => onSizePercentChange(percent)}
              className="px-4 py-2 text-[11.5px] font-semibold transition-colors"
              style={{ color: sizePercent === percent ? "var(--t-act)" : "var(--t-mut2)" }}
            >
              {percent}%
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-9 text-13" style={{ color: "var(--t-tx2)" }}>
        <label className="flex cursor-pointer items-center gap-9">
          <span
            className="inline-block h-15 w-15 rounded-4 border-[1.5px]"
            style={{ borderColor: "var(--t-mid)" }}
          />
          <Trans>Take Profit / Stop Loss</Trans>
        </label>
        <label className="flex cursor-pointer items-center gap-9">
          <span
            className="inline-block h-15 w-15 rounded-4 border-[1.5px]"
            style={{ borderColor: "var(--t-mid)" }}
          />
          <Trans>Reduce-Only</Trans>
        </label>
      </div>

      <button
        type="button"
        disabled={isEmpty}
        className={cx("rounded-full py-14 text-[14.5px] font-bold transition-[transform,box-shadow]", {
          "t-anim-ring": !isEmpty,
        })}
        style={
          isEmpty
            ? { background: "var(--t-chip)", color: "var(--t-mid)" }
            : { background: "var(--t-cta)", color: "#fff", boxShadow: "var(--t-cta-shadow)" }
        }
      >
        {isEmpty ? (
          <Trans>Enter an amount</Trans>
        ) : isBuy ? (
          <Trans>Buy / Long BTC</Trans>
        ) : (
          <Trans>Sell / Short BTC</Trans>
        )}
      </button>

      <div className="flex flex-col gap-8 border-t pt-12 text-13" style={{ borderColor: "var(--t-line)" }}>
        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Liquidation price</Trans>
          </span>
          <span className="t-nums font-semibold" style={{ color: "var(--t-short)" }}>
            {isEmpty ? "N/A" : usd(liquidationPrice)}
          </span>
        </div>
        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Margin required</Trans>
          </span>
          <span className="t-nums font-semibold" style={{ color: "var(--t-tx)" }}>
            {isEmpty ? "N/A" : usd(notional / leverage)}
          </span>
        </div>
        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Fees (maker / taker)</Trans>
          </span>
          <span className="t-nums font-semibold" style={{ color: "var(--t-tx)" }}>
            0.025% / 0.060%
          </span>
        </div>
      </div>

      {/* open position — pinned to the bottom of the column */}
      <div
        className="mt-auto flex flex-col gap-10 rounded-10 border p-13"
        style={{ background: "var(--t-panel3)", borderColor: "var(--t-line)" }}
      >
        <div className="flex items-center justify-between">
          <span
            className="t-display text-16 uppercase tracking-[1.2px]"
            style={{ color: "var(--t-deep)" }}
          >
            <Trans>Open Position</Trans>
          </span>
          <span
            className="rounded-full px-9 py-3 text-11 font-bold"
            style={{ color: "var(--t-long)", background: "var(--t-long-soft)" }}
          >
            LONG {leverage}×
          </span>
        </div>

        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Entry</Trans>
          </span>
          <span className="t-nums font-semibold" style={{ color: "var(--t-tx)" }}>
            {usd(ACCOUNT.entryPrice)}
          </span>
        </div>
        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Mark</Trans>
          </span>
          <span className="t-nums font-semibold" style={{ color: "var(--t-tx)" }}>
            {usd(price)}
          </span>
        </div>
        <div className={INFO_ROW}>
          <span style={{ color: "var(--t-mut)" }}>
            <Trans>Unrealized PNL</Trans>
          </span>
          <span
            className="t-nums font-bold"
            style={{ color: positionPnl >= 0 ? "var(--t-long)" : "var(--t-short)" }}
          >
            {positionPnl >= 0 ? "+" : "-"}
            {usd(Math.abs(positionPnl))}
          </span>
        </div>

        <button
          type="button"
          className="t-close-btn rounded-full border py-9 text-[12.5px] font-bold transition-colors"
          style={{ color: "var(--t-short)", borderColor: "var(--t-short)", background: "transparent" }}
        >
          <Trans>Close position</Trans>
        </button>
      </div>
    </section>
  );
}
